import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle, FiBell, FiCheckCircle, FiDollarSign } from 'react-icons/fi';
import { getMyContributionSummary } from '../services/api';

// v1.6.6 — Persistent status strip shown at the top of every member-portal
// page (mounted inside MemberLayout above the Outlet). Three states:
//   overdue   → red   → 'You owe $/K…' + Pay now
//   due_soon  → amber → 'K200 due on YYYY-MM-DD' + Pay now
//   up_to_date→ green → 'You're up to date on all contributions.'
// Refreshes on the 'subscription-updated' custom event so after a member
// approves a payment or an admin marks something the banner flips instantly.

const fmt = (n) => parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ContributionStatusBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    getMyContributionSummary()
      .then(r => setSummary(r.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener('subscription-updated', h);
    return () => window.removeEventListener('subscription-updated', h);
  }, []);

  if (loading || !summary || summary.status === 'none') return null;

  const usd = parseFloat(summary.total_owed_usd || 0);
  const k   = parseFloat(summary.total_owed_kwacha || 0);
  const owedText = [
    usd > 0 && `$${fmt(usd)}`,
    k   > 0 && `K${fmt(k)}`,
  ].filter(Boolean).join(' · ');

  // ── Up to date (green) ─────────────────────────────────────────────
  if (summary.status === 'up_to_date') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 16px', margin: '0 0 12px',
        background: '#f0fdf4', borderLeft: '4px solid #16a34a',
        borderRadius: 8, color: '#166534', fontSize: 13, fontWeight: 500,
      }}>
        <FiCheckCircle style={{ flexShrink: 0 }} />
        <span>{t('contribBanner.upToDate', { defaultValue: "You're up to date on all contributions. Thank you." })}</span>
      </div>
    );
  }

  const isOverdue = summary.status === 'overdue';
  const behind = summary.subscriptions_behind || [];
  const subLabels = behind.slice(0, 2).map(b => b.type_name).join(', ');
  const extra = behind.length > 2 ? ` +${behind.length - 2} more` : '';

  // ── Overdue (red) or Due soon (amber) ──────────────────────────────
  const palette = isOverdue
    ? { bg: '#fef2f2', border: '#dc2626', text: '#991b1b', icon: <FiAlertTriangle /> }
    : { bg: '#fffbeb', border: '#f59e0b', text: '#92400e', icon: <FiBell /> };

  const primaryMsg = isOverdue
    ? t('contribBanner.owed', {
        defaultValue: 'You owe {{amount}} · {{count}} overdue period{{plural}} on {{types}}{{extra}}',
        amount: owedText, count: summary.overdue_count,
        plural: summary.overdue_count === 1 ? '' : 's',
        types: subLabels, extra,
      })
    : t('contribBanner.dueSoon', {
        defaultValue: '{{amount}} due on {{date}} for {{types}}{{extra}}',
        amount: owedText, date: summary.next_due_date,
        types: subLabels, extra,
      });

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 16px', margin: '0 0 12px',
      background: palette.bg, borderLeft: `4px solid ${palette.border}`,
      borderRadius: 8, color: palette.text, fontSize: 13, fontWeight: 600,
    }}>
      <span style={{ flexShrink: 0, display: 'inline-flex' }}>{palette.icon}</span>
      <span style={{ flex: 1, lineHeight: 1.4 }}>{primaryMsg}</span>
      <button
        // v1.6.11 — when only one subscription is behind, deep-link to
        // Submit Payment with ?type= so the fund gets pre-selected and
        // locked (mirrors the 'Make Payment' button on My Contributions).
        // With multiple subs behind, we can't guess which one — send the
        // member to the plain form to pick.
        onClick={() => {
          const only = behind.length === 1 ? behind[0] : null;
          const url = only?.fund_category_id
            ? `/member/submit-payment?type=${only.fund_category_id}`
            : '/member/submit-payment';
          navigate(url);
        }}
        style={{
          flexShrink: 0, padding: '6px 14px',
          background: palette.border, color: '#fff', border: 'none',
          borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}
      >
        <FiDollarSign size={12} /> {t('contribBanner.payNow', { defaultValue: 'Pay now' })}
      </button>
    </div>
  );
};

export default ContributionStatusBanner;
